import React, { PropsWithChildren, useEffect, useState } from "react";
import { CodedProperty, ensure, getById, Optional } from "sprint-common-web";

import { CreateEntityResponse } from "../../../../../common/api/common/CreateEntityResponse";
import { CrossTypeContentSearchItem } from "../../../../../common/components/form/CrossTypeContentSearch";
import { ContentSubtype } from "../../../../../common/types/ContentSubtype";
import { DeliveryEndpointReference } from "../../../../../common/types/DeliveryEndpoint";
import { DeliveryEndpointType } from "../../../../../common/types/DeliveryEndpointType";
import { EncodeSpecification } from "../../../../../common/types/EncodeSpecification";
import { Integrator } from "../../../../../common/types/Integrator";
import { System } from "../../../../../common/types/System";
import { OrdersAirline, SystemPlatform } from "../../../../common/types/OrdersAirline";
import { Resolution } from "../../../../common/types/Resolution";
import { useOrdersModuleApi } from "../../../../common/useOrdersModuleApi";
import {
    OrderAncillaryVersionItem,
    OrderContentVersionItem,
    OrderEpisodeVersionItem,
    OrderMovieVersionItem
} from "../../types/OrderContentVersionItem";
import { mapToOrderMuxSpecificationItem, OrderMuxSpecificationItem } from "../../types/OrderMuxSpecificationItem";
import { OrderSoundtrackItem } from "../../types/OrderSoundtrackItem";
import { useDeliveryEndpoints } from "../hooks/useDeliveryEndpoints";
import { getSequencedSoundtrackIds } from "../soundtrack/soundtrack";
import { EmbeddedSubtitle, getSequencedSubtitlesIds, OriginSubtitlesFileName } from "../subtitles/subtitles";
import { CreateOrderArgs, CreateOrderContext } from "./CreateOrderContext";

type CreateOrderProviderProps = PropsWithChildren<unknown>;

export const CreateOrderProvider: React.FC<CreateOrderProviderProps> = (props: CreateOrderProviderProps) => {
    const { createOrder, getMuxSpecificationById } = useOrdersModuleApi();
    const { data: deliveryEndpoints } = useDeliveryEndpoints();

    const [contentType, setContentType] = useState<ContentSubtype>();
    const [contentDetails, setContentDetails] = useState<CrossTypeContentSearchItem>();
    const [deliveryType, setDeliveryType] = useState<DeliveryEndpointType>();

    const [contentVersion, setContentVersion] = useState<OrderContentVersionItem>();
    const [airline, setAirline] = useState<OrdersAirline>();

    const [system, setSystem] = useState<System>();
    const [integrator, setIntegrator] = useState<Integrator>();
    const [deliveryEndpoint, setDeliveryEndpoint] = useState<DeliveryEndpointReference>();
    const [encodeSpecification, setEncodeSpecification] = useState<EncodeSpecification>();
    const [muxSpecification, setMuxSpecification] = useState<OrderMuxSpecificationItem>();
    const [soundtracks, setSoundtracks] = useState<OrderSoundtrackItem[]>([]);
    const [targetResolution, setTargetResolution] = useState<Resolution>();
    const [targetFileName, setTargetFileName] = useState<string>();
    const [embeddedSubtitle, setEmbeddedSubtitle] = useState<CodedProperty>(EmbeddedSubtitle);
    const [dynamicSubtitles, setDynamicSubtitles] = useState<CodedProperty[]>([]);
    const [originSubtitlesFileNames, setOriginSubtitlesFileNames] = useState<Optional<OriginSubtitlesFileName[]>>();
    const [hasAirlineMismatch, setHasAirlineMismatch] = useState<boolean>(false);

    useEffect(() => {
        if (system && airline && deliveryEndpoints?.length) {
            setIntegrator(system.integrator);

            const systemPlatform = airline.systemPlatforms.find((item: SystemPlatform) => item.system.id === system.id);

            if (!systemPlatform) {
                return;
            }

            ensure(systemPlatform);
            ensure(systemPlatform.deliveryEndpoint);

            setDeliveryEndpoint(systemPlatform.deliveryEndpoint);

            const { type } = getById(deliveryEndpoints, systemPlatform.deliveryEndpoint.id);
            setDeliveryType(type);

            ensure(systemPlatform.muxSpecification);

            getMuxSpecificationById(systemPlatform.muxSpecification.id).then((response) => {
                const orderMuxSpecificationItem = mapToOrderMuxSpecificationItem(response);
                setMuxSpecification(orderMuxSpecificationItem);
            });
        } else {
            setIntegrator(undefined);
            setDeliveryEndpoint(undefined);
            setMuxSpecification(undefined);
        }
    }, [system, airline, deliveryEndpoints?.length]);

    const setMovieVersion = (version: OrderMovieVersionItem | undefined): void => {
        setContentVersion(version);
    };

    const setEpisodeVersion = (version: OrderEpisodeVersionItem | undefined): void => {
        setContentVersion(version);
    };

    const setAncillaryVersion = (version: OrderAncillaryVersionItem | undefined): void => {
        setContentVersion(version);
    };

    const clearContentVersion = (): void => {
        setContentVersion(undefined);
    };

    const createNewOrder = async (args: CreateOrderArgs): Promise<CreateEntityResponse> => {
        ensure(contentVersion);
        ensure(airline);
        ensure(system);
        ensure(deliveryEndpoint);
        ensure(encodeSpecification);
        ensure(embeddedSubtitle);

        const subtTargetFileName = args.subtitlesTargetFileName?.length ? args.subtitlesTargetFileName : undefined;

        return createOrder({
            content: contentVersion,
            airlineId: airline.id,
            systemId: system.id,
            embeddedSubtitleId: embeddedSubtitle.id,
            sequencedSoundtrackIds: getSequencedSoundtrackIds({ soundtracks }),
            sequencedSubtitlesIds: getSequencedSubtitlesIds({
                dynamicSubtitles,
                originSubtitlesFileNames
            }),
            encodeSpecificationId: encodeSpecification.id,
            targetResolutionId: targetResolution?.id,
            dueDate: args.dueDate,
            deliveryFolder: args.deliveryFolder,
            targetFileName: args.targetFileName,
            subtitlesTargetFileName: subtTargetFileName,
            pONumber: args.pONumber,
            startDate: args.startDate,
            isSubtitlesEmbedded: args.isSubtitlesEmbedded
        });
    };

    return (
        <CreateOrderContext.Provider
            value={{
                contentType,
                contentDetails,
                contentVersion,
                airline,
                system,
                integrator,
                deliveryType,
                deliveryEndpoint,
                encodeSpecification,
                muxSpecification,
                soundtracks,
                embeddedSubtitle,
                dynamicSubtitles,
                targetResolution,
                targetFileName,
                originSubtitlesFileNames,
                hasAirlineMismatch,
                setContentType,
                setContentDetails,
                setMovieVersion,
                setEpisodeVersion,
                setAncillaryVersion,
                setAirline,
                setSystem,
                setEncodeSpecification,
                setSoundtracks,
                setEmbeddedSubtitle,
                setDynamicSubtitles,
                setTargetResolution,
                setTargetFileName,
                clearContentVersion,
                createNewOrder,
                setOriginSubtitlesFileNames,
                setHasAirlineMismatch
            }}
        >
            {props.children}
        </CreateOrderContext.Provider>
    );
};
